import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { TruncatedTooltipValue } from '@/components/layout/TruncatedTooltipValue';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { ModalVariant } from '@/features/configuration-management/location/table/Column';
import { ManagePointCategoryContainer } from '@/features/points-management/point-setup-forms/customer/ManagePointCategoryContainer';
import { useDynamicLocale } from '@/hoslog-intl/Translation';
import { EditIcon, LockCloseIcon, LockOpenIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { ApiResponse } from '@/react-query/context/ApiClientContextController';
import { StandardizedApiError } from '@/services/client/types';
import {
  CustomerPointSettingListResponse,
  ManagePointSettingStatusPayload,
  POINT_SETTING_STATUS,
} from '@/services/pointConfiguration/pointConfiguration.types';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { ColumnDef } from '@tanstack/react-table';
import { ReactNode } from 'react';
import { Button } from 'ui/button';

type ModalSize = 'sm' | 'md' | 'lg';

type OnOpenProps = <T extends string>(config: {
  title: T;
  modalTitle: string | null;
  okText: string | ReactNode;
  component: ReactNode;
  cancelText?: string | ReactNode;
  size?: ModalSize;
  onCancel?: () => void;
}) => void;
type OpenConfirmationProps = (data: {
  title: string;
  description?: string | undefined;
  cancelLabel: string;
  actionLabel: string;
  content?: React.ReactNode;
  size?: ModalSize;
  variant?: ModalVariant;
  onAction?: () => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  isSubmitting?: 'submit' | 'cancel' | null;
}) => void;

export function getColumns(
  onOpen: OnOpenProps,
  openConfirmation: OpenConfirmationProps,
  t: ReturnType<typeof useDynamicLocale>['t'],
  updateStatus: UseMutateAsyncFunction<ApiResponse, StandardizedApiError, ManagePointSettingStatusPayload, unknown>,
  canViewReferralStageList: boolean,
): ColumnDef<CustomerPointSettingListResponse>[] {
  return [
    {
      id: 'sn',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
      cell: ({ row, table }) => (
        <div className="max-w-[10rem] truncate text-[12px] font-[500]">{getSerialNumber(table, row.index)}</div>
      ),
      enableSorting: false,
    },
    {
      accessorKey: 'category',
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title={t('pointManagement.pointsConfiguration.customer.table.categoryName')}
        />
      ),
      cell: ({ row }) => {
        const isReview =
          (row.original.programCode ?? '').toUpperCase().includes('REVIEW') ||
          (row.original.category ?? '').toLowerCase().includes('review');

        if (isReview) {
          return (
            <div className="cursor-not-allowed inline-block opacity-50">
              <TruncatedTooltipValue
                content={row.getValue<string>('category')}
                className="max-w-[20rem] text-[12px] font-medium underline text-info cursor-not-allowed"
              />
            </div>
          );
        }

        const canNavigate = canViewReferralStageList && row.original.status === POINT_SETTING_STATUS.ACTIVE;

        return canNavigate ? (
          <Link
            to="/points-management/points-configuration/customer/$programId"
            params={{ programId: row.original.programId }}
            search={() => {
              const url = new URL(window.location.href);
              if (!url.searchParams.get('activeTab')) {
                url.searchParams.set('activeTab', 'customer');
              }
              return { breadcrumbReturnTo: url.toString() };
            }}
          >
            <TruncatedTooltipValue
              content={row.getValue<string>('category')}
              className="max-w-[20rem] text-[12px] font-medium text-info underline cursor-pointer"
            />
          </Link>
        ) : (
          <div className="cursor-not-allowed inline-block">
            <TruncatedTooltipValue
              content={row.getValue<string>('category')}
              className="max-w-[20rem] text-[12px] font-medium text-muted-foreground underline cursor-not-allowed"
            />
          </div>
        );
      },
      meta: {
        label: t('common.page.tableColumn.categoryName'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'categoryType',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.categoryType')} />
      ),
      cell: () => (
        <div className="max-w-[20rem] truncate text-[12px] font-[500]">{t('common.page.tableColumn.default')}</div>
      ),
      meta: {
        label: t('common.page.tableColumn.categoryType'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'description',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.description')} />
      ),
      cell: ({ row }) => (
        <TruncatedTooltipValue
          content={row.getValue<string>('description')}
          className="max-w-[20rem] text-[12px] font-medium"
        />
      ),
      meta: {
        label: t('common.page.tableColumn.description'),
      },
      enableHiding: false,
    },
    {
      accessorKey: 'createdBy',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdBy')} />,
      cell: ({ row }) => <div className="text-[12px] font-[500]">{row.getValue('createdBy')} </div>,
      enableHiding: false,
    },
    {
      id: 'createdDate',
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),
      cell: ({ row }) => (
        <div className="text-[12px] font-[500] whitespace-nowrap">
          {formatDate(Number(row.getValue('createdDate')))}{' '}
        </div>
      ),
      meta: {
        label: t('common.page.tableColumn.createdDate'),
      },
      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.status')} />,
      cell: ({ row }) =>
        row.original.status === POINT_SETTING_STATUS.ACTIVE ? (
          <div className="text-success text-[12px] font-[500]">{t('common.page.table.active')}</div>
        ) : (
          <div className="text-destructive text-[12px] font-[500]">{t('common.page.table.blocked')}</div>
        ),

      enableHiding: false,
      enableColumnFilter: false,
    },
    {
      id: 'actions',
      accessorKey: 'actions',
      header: ({ column }) => (
        <DataTableColumnHeader
          className="whitespace-nowrap"
          column={column}
          title={t('common.page.tableColumn.action')}
        />
      ),
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        const isActive = row.original.status === POINT_SETTING_STATUS.ACTIVE;
        return (
          <div className="flex gap-4 items-center">
            {isActive && (
              <Can rule={{ allOf: [PERMS.point.management.customer.detail, PERMS.point.management.customer.manage] }}>
                <EditIcon
                  className="text-foreground-subtle size-4 hover:cursor-pointer"
                  onClick={() =>
                    onOpen({
                      title: 'pointManagement.pointsConfiguration.managePointCategory.title',
                      okText: t('pointManagement.pointsConfiguration.managePointCategory.button.submit'),
                      cancelText: t('common.page.button.cancel'),
                      size: 'lg',
                      modalTitle: 'pointManagement.pointsConfiguration.managePointCategory.title',
                      component: (
                        <ManagePointCategoryContainer
                          programId={row.original.programId}
                          categoryId={row.original.categoryId}
                          programCode={row.original.programCode}
                        />
                      ),
                    })
                  }
                />
              </Can>
            )}
            <Can rule={{ allOf: [PERMS.point.management.customer.statusUpdate] }}>
              <Button
                variant="ghost"
                className="p-0"
                onClick={() => {
                  openConfirmation({
                    title: t('common.page.modal.confirm'),
                    description: isActive ? t('common.page.modal.blockMessage') : t('common.page.modal.unblockMessage'),
                    cancelLabel: t('common.page.modal.no'),
                    actionLabel: t('common.page.modal.submit'),
                    size: 'lg',
                    variant: 'warning',
                    onAction: async () => {
                      const payload = {
                        categoryId: row.original.categoryId,
                        status:
                          row.original.status === POINT_SETTING_STATUS.ACTIVE
                            ? POINT_SETTING_STATUS.BLOCKED
                            : POINT_SETTING_STATUS.ACTIVE,
                      };
                      await updateStatus(payload);
                    },
                    onCancel: () => {},
                  });
                }}
              >
                {isActive ? (
                  <LockCloseIcon className="text-destructive size-4" />
                ) : (
                  <LockOpenIcon className="text-info size-4" />
                )}
              </Button>
            </Can>
          </div>
        );
      },
      size: 40,
    },
  ];
}
